/**
 * useDialogSpatialNav — arrow-key spatial navigation scoped to a modal dialog
 * (CLAUDE.md standing rule 14, ADR-0013).
 *
 * The app's main spatial nav (AppShell.onContentKeyDown) deliberately YIELDS the
 * moment a modal is open ("a dialog owns its keys"), so without this, ↑/↓/←/→ are
 * dead inside every <AppDialog> — the user could only Tab (user report 2026-09-22:
 * arrows not working inside the Sample Condition Check + Note popups).
 *
 * This mirrors the main nav's contract EXACTLY, but scoped to the dialog element so
 * focus can never escape the modal:
 *   · ↑/↓/←/→ move focus to the geometrically nearest control in that direction
 *     (cross-axis distance weighted ×3 to prefer the same row / column).
 *   · A control that OWNS the arrow keeps it — a date/time field, textarea /
 *     contenteditable, native <select>, radio, an OPEN combo/menu, a data-grid row
 *     (↑/↓), and a text/number field while the caret still has room (←/→).
 *   · A child that already handled the key (e.defaultPrevented) is left alone, and
 *     a modifier combo (Alt+↑/↓ whole-value date step, etc.) is not touched.
 * It is purely additive: dialogs have NO arrow nav today, so nothing that works
 * now changes.
 */
import { useEffect, type RefObject } from 'react';

/** Focus the geometrically nearest focusable control inside `scope` in `dir`. */
function focusInDirection(from: HTMLElement, dir: 'up' | 'down' | 'left' | 'right', scope: HTMLElement): boolean {
  const SEL = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[contenteditable="true"],[role="combobox"]:not([aria-disabled="true"]),[role="listbox"]:not([aria-disabled="true"]),[tabindex]:not([tabindex="-1"])';
  const fr = from.getBoundingClientRect();
  const fx = fr.left + fr.width / 2, fy = fr.top + fr.height / 2;
  let best: HTMLElement | null = null, bestScore = Infinity;
  for (const el of Array.from(scope.querySelectorAll<HTMLElement>(SEL))) {
    if (el === from || from.contains(el) || el.contains(from)) continue;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;                 // not rendered
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    const dx = cx - fx, dy = cy - fy;
    let along: number, across: number;
    if (dir === 'down') { if (dy <= 1) continue; along = dy; across = Math.abs(dx); }
    else if (dir === 'up') { if (dy >= -1) continue; along = -dy; across = Math.abs(dx); }
    else if (dir === 'right') { if (dx <= 1) continue; along = dx; across = Math.abs(dy); }
    else { if (dx >= -1) continue; along = -dx; across = Math.abs(dy); }
    const score = along + across * 3;                              // strongly prefer the same row / column
    if (score < bestScore) { bestScore = score; best = el; }
  }
  if (!best) return false;
  best.focus({ preventScroll: true });
  best.scrollIntoView?.({ block: 'nearest' });
  if (best instanceof HTMLInputElement && /^(text|search|url|tel|email|password|number)$/.test(best.type)) {
    try { const n = best.value.length; best.setSelectionRange(n, n); } catch { /* some types disallow selection */ }
  }
  return true;
}

/** True when the focused control itself consumes the pressed arrow (keeps it). */
function ownsArrow(el: HTMLElement, key: string): boolean {
  const tag = el.tagName;
  const type = ((el as HTMLInputElement).type || '').toLowerCase();
  const role = el.getAttribute('role');
  const inGridRow = !!el.closest('.adg tbody tr, .mlv-nav');
  const isTextArea = tag === 'TEXTAREA' || el.isContentEditable;
  const isSelect = tag === 'SELECT';
  const isRadio = tag === 'INPUT' && type === 'radio';
  const isNumber = tag === 'INPUT' && type === 'number';
  const isDateLike = tag === 'INPUT' && /^(date|time|month|week|datetime-local)$/.test(type);
  const isTextInput = tag === 'INPUT'
    && !/^(checkbox|radio|button|submit|reset|range|color|file|number|date|time|month|week|datetime-local)$/.test(type);
  const isCombo = role === 'combobox' || role === 'listbox';
  const comboOpen = el.getAttribute('aria-expanded') === 'true';

  // Controls that fully OWN the arrows (native editing / value / group / segments).
  if (isTextArea || isSelect || isRadio || isDateLike || (isCombo && comboOpen)) return true;

  if (key === 'ArrowUp' || key === 'ArrowDown') {
    // number steps · a closed combo changes its value · a grid row moves its rows
    return isNumber || (isCombo && !comboOpen) || inGridRow;
  }
  if (isTextInput || isNumber) {
    // a text/number field keeps ← / → while the caret still has room to move
    try {
      const inp = el as HTMLInputElement;
      const atStart = inp.selectionStart === 0 && inp.selectionEnd === 0;
      const atEnd = inp.selectionStart === inp.value.length && inp.selectionEnd === inp.value.length;
      return key === 'ArrowLeft' ? !atStart : !atEnd;
    } catch { return false; }
  }
  return false;
}

/**
 * Arrow spatial-nav scoped to the dialog element in `scopeRef`, active while `open`.
 * Attaches a keydown listener on the dialog so focus stays within the modal.
 */
export function useDialogSpatialNav(scopeRef: RefObject<HTMLElement | null>, open: boolean): void {
  useEffect(() => {
    if (!open) return;
    const scope = scopeRef.current;
    if (!scope) return;
    const onKey = (e: KeyboardEvent) => {
      const key = e.key;
      if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') return;
      if (e.defaultPrevented) return;                               // a child already handled it
      if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return; // leave Alt+↑/↓ (date step) etc.
      const el = e.target as HTMLElement | null;
      if (!el || !scope.contains(el)) return;
      if (ownsArrow(el, key)) return;
      const dir = key === 'ArrowUp' ? 'up' : key === 'ArrowDown' ? 'down' : key === 'ArrowLeft' ? 'left' : 'right';
      if (focusInDirection(el, dir, scope)) e.preventDefault();
    };
    scope.addEventListener('keydown', onKey);
    return () => scope.removeEventListener('keydown', onKey);
  }, [open, scopeRef]);
}
